
import { supabase } from "@/integrations/supabase/client";
import { fetchWithRetry } from "./fetchWithRetry";
import { copyRoutineWithExercises } from "@/components/routines/search/hooks/utils/copyRoutine";

/**
 * Handles adding a routine to a personal plan by creating a copy
 * to ensure isolation between different users' plans
 */
export const handleAddRoutineToPersonalPlan = async (
  routineId: string, 
  planId: string, 
  userId: string
) => {
  // Get original routine details
  const { data: originalRoutine, error: routineError } = await supabase
    .from("routines")
    .select(`
      name,
      exercises (
        name, sets, reps, weight, set_data, position, exercise_rest_time, set_rest_time, video_url
      ),
      routine_days (day)
    `)
    .eq("id", routineId)
    .single();
    
  if (routineError) {
    console.error("Error fetching original routine:", routineError);
    throw routineError;
  }

  // Create a copy of the routine using our utility function
  const newRoutine = await copyRoutineWithExercises({
    id: routineId,
    name: originalRoutine.name,
    exercises: originalRoutine.exercises,
    routine_days: originalRoutine.routine_days
  }, userId);

  // Compute next position for sequential ordering
  const nextPosition = await getNextPlanPosition(planId);

  // Link the new routine copy to the plan
  const { error: insertError } = await fetchWithRetry(async () => {
    return supabase
      .from("workout_plan_routines")
      .insert({
        workout_plan_id: planId,
        routine_id: newRoutine.id,
        position: nextPosition,
      });
  });

  if (insertError) throw insertError;
  
  console.log("Created isolated copy of routine:", newRoutine.id);
  return newRoutine.id;
};

/**
 * Handles adding an existing routine to a shared plan
 * without creating a copy
 */
export const handleAddRoutineToSharedPlan = async (routineId: string, planId: string) => {
  const nextPosition = await getNextPlanPosition(planId);

  const { error: insertError } = await fetchWithRetry(async () => {
    return supabase
      .from("workout_plan_routines")
      .insert({
        workout_plan_id: planId,
        routine_id: routineId,
        position: nextPosition,
      });
  });

  if (insertError) throw insertError;
  return routineId;
};

/**
 * Returns the next available position for a routine added to the given plan.
 */
const getNextPlanPosition = async (planId: string): Promise<number> => {
  const { data, error } = await supabase
    .from("workout_plan_routines")
    .select("position")
    .eq("workout_plan_id", planId)
    .order("position", { ascending: false })
    .limit(1);

  if (error) {
    console.warn("Could not compute next position, defaulting to 0:", error);
    return 0;
  }

  return ((data?.[0]?.position ?? -1) as number) + 1;
};
